/**
 * Navigation constants — the single mapping between the page ids the UI passes
 * around (`onNavigate('collection')`) and the URLs the router serves.
 *
 * The slugs are deliberately identical to the ids the app used before it had
 * real routing, so every existing `onNavigate(id)` / `data-tutorial` id keeps
 * working: `/collection`, `/expeditions-hub`, `/task-board`…
 *
 * A handful of legacy ids are kept as aliases of a canonical page. Navigating
 * to an alias goes straight to the canonical path; typing the alias URL by hand
 * is redirected by the matching route in AppRoutes.
 */

/** The page id rendered at `/`. */
export const HOME_PAGE_ID = 'home';

/** Legacy page id -> canonical page id. */
export const PAGE_ALIASES = {
    cards: 'collection',
    tokens: 'collection',
    store: 'shop',
    minigame: 'minigames',
    competition: 'minigames',
    'community-presets': 'reference',
};

/**
 * Resolve a page id (possibly a legacy alias) to its canonical id.
 * @param {string} pageId
 * @returns {string}
 */
export function canonicalPageId(pageId) {
    return PAGE_ALIASES[pageId] || pageId;
}

/**
 * URL path for a page id. Unknown ids fall through to `/<id>`, which the
 * catch-all route sends back home.
 * @param {string} pageId
 * @returns {string}
 */
export function pathForPage(pageId) {
    const id = canonicalPageId(pageId);
    return id === HOME_PAGE_ID ? '/' : `/${id}`;
}

/**
 * Page id for a URL path — the inverse of `pathForPage`, used for the sidebar's
 * active state and for the tutorial's "are we on the right page yet" checks.
 * @param {string} pathname
 * @returns {string}
 */
export function pageIdForPath(pathname) {
    const [firstSegment] = pathname.replace(/^\/+/, '').split('/');
    if (!firstSegment) return HOME_PAGE_ID;
    return canonicalPageId(firstSegment);
}
